import React from 'react';
import propTypes from 'prop-types';
import StateUpdateChip from './StateUpdateChip';

// one section of state chips inside CarrierUpdatesPanel (used for both
// "Expansions" and "Exits"). renders the title, helper copy, and the chip
// row. each chip colors itself by the state-level aggregated status so the
// chip and the "all available states" grid never disagree for the same state.
/**
 * @param title - section heading
 * @param helper - italic subtitle explaining the interaction
 * @param updates - array of normalized oep state updates from carrierFactory
 * @param statusByCode - map of stateCode → aggregated status (Added/Updated/
 *   Removed) built by buildStateStatusMap. the chip prefers this over the
 *   per-row status so e.g. a new state with both a Whole State and a Counties
 *   Only expansion row still reads green ("Added") instead of amber.
 * @param selectedKey - key of the currently-selected chip (or null)
 * @param makeHandler - factory returning the onClick for a given update
 * @param emptyText - fallback copy when `updates` is empty
 */
export default function ChipSection({
  title,
  helper,
  updates,
  statusByCode,
  selectedKey,
  makeHandler,
  emptyText
}) {
  return (
    <section className='min-w-0'>
      <h4 className='m-0 mb-1 text-lg font-semibold text-black'>{title}</h4>
      <p className='m-0 mb-2 text-xs italic text-gray-500'>{helper}</p>
      {updates.length === 0 ? (
        <p className='m-0 text-xs text-gray-400'>{emptyText}</p>
      ) : (
        <div className='flex flex-wrap gap-2'>
          {updates.map(update => (
            <StateUpdateChip
              key={update.id}
              stateCode={update.stateCode}
              status={statusByCode?.[update.stateCode] || update.status}
              stateName={update.stateName}
              isSelected={selectedKey === update.id}
              onClick={makeHandler(update)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

ChipSection.propTypes = {
  title: propTypes.string.isRequired,
  helper: propTypes.string.isRequired,
  updates: propTypes.array.isRequired,
  statusByCode: propTypes.object,
  selectedKey: propTypes.string,
  makeHandler: propTypes.func.isRequired,
  emptyText: propTypes.string.isRequired
};
